import { useMemo } from 'react';
import {
  Box,
  Flex,
  Grid,
  Text,
  Spinner,
  Center,
  Link,
  Divider,
  Alert,
  AlertIcon,
  Button,
} from '@chakra-ui/react';
import SubscriptionOption from './SubscriptionOption';
import StaticSubscriptionOption from './StaticSubscriptionOption';
import BillingIntervalToggle from './BillingIntervalToggle';
import {
  monthlyAmountMinorUnits,
  formatGbpFromMinorUnits,
  annualSavingsVsMonthlyPrice,
  maxAnnualDiscountPercentAcrossPlans,
} from '../../../utils/subscriptionUtils';

const PlanSelectionGrid = ({
  plans = [],
  isLoading,
  selectedInterval = 'month',
  onIntervalChange,
  onPlanSelect,
  onPlanSelected,
  setCancelPlan,
  subscription,
  currentPlanData,
  title = 'Select a plan',
  staticPlans = [],
  customPlanRequest,
  onOpenRequestCustomPlan,
  handleBookDemo,
  errorMessage,
  showFooter = false,
}) => {
  const findMatchingPrice = (prices) => {
    if (!prices || !prices.length) return null;
    const matchingPrice = prices.find((p) => p.interval === selectedInterval);
    return matchingPrice || prices[0];
  };

  const subscriptionInterval = subscription?.plan?.interval;

  const currentPlanCardProps = useMemo(() => {
    if (!currentPlanData || !subscriptionInterval) return null;

    const priceFromCatalog = currentPlanData.prices?.find(
      (p) => p.interval === subscriptionInterval
    );

    let priceObj = priceFromCatalog || null;

    if (!priceObj) {
      const sp = currentPlanData.selected_price;
      const spInterval = sp?.interval || currentPlanData.selected_interval;
      if (sp?.amount != null && spInterval === subscriptionInterval) {
        priceObj = {
          amount: sp.amount,
          interval: subscriptionInterval,
          id: sp.id,
        };
      }
    }

    if (!priceObj && currentPlanData.prices?.length) {
      priceObj = currentPlanData.prices[0];
    }

    if (!priceObj || priceObj.amount == null) return null;

    const savings =
      subscriptionInterval === 'year'
        ? annualSavingsVsMonthlyPrice(currentPlanData.prices)
        : null;
    const compareAtPriceFormatted =
      savings?.listMonthlyMinor != null
        ? formatGbpFromMinorUnits(savings.listMonthlyMinor, {
            wholePounds: true,
          })
        : undefined;

    const billingPriceFormatted = formatGbpFromMinorUnits(priceObj.amount);
    const billedPeriodCaption =
      priceObj.interval === 'year'
        ? `${formatGbpFromMinorUnits(priceObj.amount, {
            wholePounds: true,
          })} billed annually`
        : undefined;

    return {
      label: currentPlanData.name,
      planId: currentPlanData.stripeProductId,
      priceId: priceObj.id,
      monthlyMinor: monthlyAmountMinorUnits(priceObj),
      compareAtPriceFormatted,
      billingPriceFormatted,
      billedPeriodCaption,
      features: currentPlanData.features || [],
      rank: currentPlanData.rank,
    };
  }, [currentPlanData, subscriptionInterval]);

  const showCurrentPlanCard = Boolean(
    currentPlanCardProps &&
    subscriptionInterval &&
    selectedInterval === subscriptionInterval
  );

  const hasYearlyPrices = plans.some((p) =>
    p.prices?.some((pr) => pr.interval === 'year')
  );

  const maxAnnualSavePercent = useMemo(
    () => maxAnnualDiscountPercentAcrossPlans(plans),
    [plans]
  );

  const plansWithAnnualSavingsCount = useMemo(
    () =>
      plans.filter((p) => annualSavingsVsMonthlyPrice(p.prices) != null).length,
    [plans]
  );

  const annualSaveHint = useMemo(() => {
    if (maxAnnualSavePercent <= 0) return undefined;
    return plansWithAnnualSavingsCount > 1
      ? `Save up to ${maxAnnualSavePercent}%`
      : `Save ${maxAnnualSavePercent}%`;
  }, [maxAnnualSavePercent, plansWithAnnualSavingsCount]);

  return (
    <>
      <Flex
        direction={{ base: 'column', md: 'row' }}
        justifyContent="space-between"
        align={{ base: 'stretch', md: 'flex-start' }}
        mt="10px"
        gap={{ base: 5, md: 6 }}
        w="full"
      >
        <Flex
          direction="column"
          alignItems={{ base: 'center', md: 'flex-start' }}
          gap={2}
          minW={0}
          flex={1}
        >
          <Text textStyle="heading3" textAlign={{ base: 'center', md: 'left' }}>
            {title}
          </Text>
          <Link
            href="https://passentry.com/pricing"
            textStyle="headingFamilyMedium"
            fontSize="14px"
            textDecoration="underline"
            whiteSpace="nowrap"
            alignSelf={{ base: 'center', md: 'flex-start' }}
            _hover={{ color: 'orangePrimary' }}
            isExternal
          >
            See full comparison
          </Link>
        </Flex>
        {onIntervalChange && hasYearlyPrices && (
          <Flex
            direction="column"
            alignItems="center"
            gap={2}
            flexShrink={0}
            alignSelf={{ base: 'center', md: 'flex-start' }}
            ml={{ base: 0, md: 'auto' }}
          >
            <BillingIntervalToggle
              value={selectedInterval}
              onChange={onIntervalChange}
              size="sm"
            />
            {annualSaveHint && (
              <Text fontSize="sm" lineHeight="short" textAlign="center" px={1}>
                <Text as="span" fontWeight="bold" color="orangePrimary">
                  {annualSaveHint}
                </Text>
                <Text as="span" color="textGrey" fontWeight="normal">
                  {' '}
                  with annual plan
                </Text>
              </Text>
            )}
          </Flex>
        )}
      </Flex>
      <Divider mt="10px" border="1px solid" borderColor="lightGrey" />

      <Flex direction="column" gap={3} alignItems="center" w="full" mt={8}>
        {isLoading ? (
          <Center mt={3}>
            <Spinner size="xl" />
          </Center>
        ) : (
          <Grid
            templateColumns={{
              base: '1fr',
              md: 'repeat(auto-fill, minmax(260px, 1fr))',
            }}
            gap={6}
            w="100%"
          >
            {showCurrentPlanCard && currentPlanCardProps && (
              <Box>
                <SubscriptionOption
                  label={currentPlanCardProps.label}
                  price={formatGbpFromMinorUnits(
                    currentPlanCardProps.monthlyMinor,
                    { wholePounds: true }
                  )}
                  compareAtPrice={currentPlanCardProps.compareAtPriceFormatted}
                  billingPriceFormatted={
                    currentPlanCardProps.billingPriceFormatted
                  }
                  billedPeriodCaption={currentPlanCardProps.billedPeriodCaption}
                  planId={currentPlanCardProps.planId}
                  priceId={currentPlanCardProps.priceId}
                  features={currentPlanCardProps.features}
                  interval={subscriptionInterval}
                  setSelectedPlan={() => {}}
                  isCurrentPlan
                  subscription={subscription}
                  planRank={currentPlanCardProps.rank}
                  isUpgrade={false}
                  isDowngrade={false}
                />
              </Box>
            )}

            {plans.map((option, index) => {
              const price = findMatchingPrice(option.prices);

              if (
                !price ||
                (selectedInterval &&
                  price.interval !== selectedInterval &&
                  !option.prices.some((p) => p.interval === selectedInterval))
              ) {
                return null;
              }

              const displayInterval = price?.interval || 'month';
              const monthlyMinor = monthlyAmountMinorUnits(price);
              const periodPriceFormatted = formatGbpFromMinorUnits(
                price?.amount
              );

              const annualSavings =
                displayInterval === 'year'
                  ? annualSavingsVsMonthlyPrice(option.prices)
                  : null;
              const compareAtPriceFormatted =
                annualSavings?.listMonthlyMinor != null
                  ? formatGbpFromMinorUnits(annualSavings.listMonthlyMinor, {
                      wholePounds: true,
                    })
                  : undefined;

              const billedPeriodCaption =
                displayInterval === 'year' && price?.amount != null
                  ? `${formatGbpFromMinorUnits(price.amount, {
                      wholePounds: true,
                    })} billed annually`
                  : undefined;

              let isCurrentActivePlan = false;
              let isUpgrade = false;
              let isDowngrade = false;

              if (subscription?.plan) {
                const planNameMatches =
                  option.name === subscription.planName ||
                  option.name === subscription.plan?.name ||
                  option.name === currentPlanData?.name;

                isCurrentActivePlan =
                  planNameMatches &&
                  (displayInterval === subscription.interval ||
                    displayInterval === subscription.plan?.interval) &&
                  !subscription.canResubscribe;

                let currentRank = 0;
                if (subscription.plan?.rank !== undefined) {
                  const parsed = parseInt(subscription.plan.rank, 10);
                  currentRank = isNaN(parsed) ? 0 : parsed;
                }

                let thisRank = 0;
                if (option.rank !== undefined) {
                  const parsed = parseInt(option.rank, 10);
                  thisRank = isNaN(parsed) ? 0 : parsed;
                }

                const currentInterval = subscription.plan?.interval || 'month';

                const isSamePlanMonthToYearUpgrade =
                  planNameMatches &&
                  currentInterval === 'month' &&
                  displayInterval === 'year';

                const isSamePlanYearToMonthDowngrade =
                  planNameMatches &&
                  currentInterval === 'year' &&
                  displayInterval === 'month';

                isDowngrade =
                  (currentRank > 0 && thisRank > 0 && thisRank < currentRank) ||
                  isSamePlanYearToMonthDowngrade;

                isUpgrade =
                  (currentRank > 0 && thisRank > 0 && thisRank > currentRank) ||
                  isSamePlanMonthToYearUpgrade;
              }

              if (isCurrentActivePlan && showCurrentPlanCard) {
                return null;
              }

              return (
                <Box key={index}>
                  <SubscriptionOption
                    label={option.name}
                    price={formatGbpFromMinorUnits(monthlyMinor, {
                      wholePounds: true,
                    })}
                    compareAtPrice={compareAtPriceFormatted}
                    billingPriceFormatted={periodPriceFormatted}
                    billedPeriodCaption={billedPeriodCaption}
                    priceId={price.id}
                    planId={option.stripeProductId}
                    features={option.features || []}
                    finalPayment={option.finalPayment}
                    setSelectedPlan={(planData) => {
                      if (isDowngrade) return;
                      onPlanSelect(option, price, planData);
                    }}
                    setCancelPlan={setCancelPlan}
                    interval={displayInterval}
                    disabled={isDowngrade}
                    subscription={subscription}
                    planRank={option.rank}
                    isUpgrade={isUpgrade}
                    isDowngrade={isDowngrade}
                    onOpenUpdatePlanModal={onPlanSelected}
                  />
                </Box>
              );
            })}

            {staticPlans.map((plan) => (
              <Box key={plan.id}>
                <StaticSubscriptionOption
                  id={plan.id}
                  onRequestCustomPlan={onOpenRequestCustomPlan}
                  label={plan.name}
                  buttonText={plan.buttonText}
                  handleBookDemo={handleBookDemo}
                  customPlanRequest={customPlanRequest}
                  interval={selectedInterval}
                />
              </Box>
            ))}
          </Grid>
        )}
      </Flex>

      {errorMessage && (
        <Alert status="error" borderRadius="md" mt={6}>
          <AlertIcon />
          {errorMessage}
        </Alert>
      )}

      {showFooter && (
        <Text
          textStyle="bodySmall"
          color="#465467"
          fontSize="16px"
          textAlign="center"
          mt={10}
        >
          Not sure what plan is right for you?{' '}
          <Button variant="link" onClick={handleBookDemo}>
            Talk to us.
          </Button>
        </Text>
      )}
    </>
  );
};

export default PlanSelectionGrid;
